/**
 * Agent Context Provider
 * 
 * React context for providing agent system access throughout the app.
 * 
 * Flow:
 * 1. AltairAgent calls setConfig() with userData and voiceGender
 * 2. User presses connect button in ControlTray
 * 3. ControlTray calls connect() which uses the stored config
 */

import React, { createContext, FC, ReactNode, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { useAgentAPI, UseAgentAPIResult, UseAgentAPIConfig } from '../hooks/use-agent-api';
import { logger } from '../utils/logger';

// Context value includes the API result plus initialization function
interface AgentContextValue extends UseAgentAPIResult {
  initialize: (config: UseAgentAPIConfig) => Promise<void>;
  isInitialized: boolean;
  isConnecting: boolean;
  // New: Set config without connecting (AltairAgent calls this)
  setConfig: (config: UseAgentAPIConfig) => void;
  // New: Connect using stored config (ControlTray calls this)
  connect: () => Promise<void>;
  // New: Check if config is ready
  hasConfig: boolean;
}

const AgentContext = createContext<AgentContextValue | undefined>(undefined);

export interface AgentProviderProps {
  children: ReactNode;
  // Legacy mode: Direct API key (not recommended for production)
  apiKey?: string;
  // Secure mode: Backend URL for ephemeral token generation (recommended)
  backendUrl?: string;
}

export const AgentProvider: FC<AgentProviderProps> = ({ apiKey, backendUrl, children }) => {
  // Validate that at least one auth method is provided
  if (!apiKey && !backendUrl) {
    throw new Error('AgentProvider requires either apiKey or backendUrl');
  }
  
  const agentAPI = useAgentAPI(backendUrl ? { backendUrl } : { apiKey: apiKey! });
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasConfig, setHasConfig] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const pendingConfigRef = useRef<UseAgentAPIConfig | null>(null);
  const isConnectingRef = useRef(false);

  // Always keep a ref to the latest agentAPI so stable callbacks don't capture stale closures.
  // Assigning in render (not in useEffect) means the ref is current before any callbacks fire.
  const agentAPIRef = useRef(agentAPI);
  agentAPIRef.current = agentAPI;

  // Set config without connecting - called by AltairAgent
  const setConfig = useCallback((config: UseAgentAPIConfig) => {
    logger.connection('[AgentProvider] Config set, ready to connect');
    pendingConfigRef.current = config;
    setHasConfig(true);
  }, []);
  
  // Connect using stored config - called by ControlTray's connect button.
  // Stable (no changing deps) so it never causes downstream effect re-fires.
  const connect = useCallback(async () => {
    if (agentAPIRef.current.connected) {
      logger.warn('[AgentProvider] Already connected');
      return;
    }
    if (isConnectingRef.current) {
      logger.warn('[AgentProvider] Connection already in progress');
      return;
    }
    if (!pendingConfigRef.current) {
      logger.error('[AgentProvider] No config set - cannot connect');
      return;
    }
    try {
      isConnectingRef.current = true;
      setIsConnecting(true);
      logger.connection('[AgentProvider] Connecting with stored config...');
      await agentAPIRef.current.connect(pendingConfigRef.current);
      setIsInitialized(true);
      logger.connection('[AgentProvider] Agent system connected');
    } catch (error) {
      logger.error('[AgentProvider] Connection failed:', error);
    } finally {
      isConnectingRef.current = false;
      setIsConnecting(false);
    }
  }, []); // stable — reads latest agentAPI via ref
  
  // Disconnect and reset state - allows reconnecting.
  // Stable (no changing deps) — this is the critical fix for the idle-timer render loop.
  const disconnect = useCallback(async () => {
    logger.connection('[AgentProvider] Disconnecting...');
    await agentAPIRef.current.disconnect();
    setIsInitialized(false);
    logger.connection('[AgentProvider] Disconnected - ready to reconnect');
  }, []); // stable — reads latest agentAPI via ref
  
  // Initialize directly with config (legacy - for backwards compatibility)
  const initialize = useCallback(async (config: UseAgentAPIConfig) => {
    if (agentAPIRef.current.connected) {
      logger.warn('[AgentProvider] Already initialized');
      return;
    }
    pendingConfigRef.current = config;
    setHasConfig(true);
    logger.connection('[AgentProvider] Initializing agent system...');
    await agentAPIRef.current.connect(config);
    setIsInitialized(true);
    logger.connection('[AgentProvider] Agent system initialized');
  }, []); // stable — reads latest agentAPI via ref
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isInitialized) {
        agentAPI.disconnect();
      }
    };
  }, [isInitialized]);
  
  const value: AgentContextValue = {
    ...agentAPI,
    initialize,
    isInitialized,
    isConnecting,
    setConfig,
    connect,
    disconnect, // Override with our version that resets state
    hasConfig,
  };
  
  return (
    <AgentContext.Provider value={value}>
      {children}
    </AgentContext.Provider>
  );
};

export const useAgentContext = (): AgentContextValue => {
  const context = useContext(AgentContext);
  if (!context) {
    throw new Error('useAgentContext must be used within an AgentProvider');
  }
  return context;
};

export default AgentProvider;

